import { useState, useEffect } from "react";
import { 
  Shield, Building2, Users, Search, Save, RefreshCw, AlertTriangle, 
  LayoutDashboard, UserCircle, Calendar, CheckSquare, Zap, BarChart3, 
  Settings, Plug, Clock, Wallet, FileText, Briefcase, Eye, EyeOff,
  ChevronDown, History, User, X, MessageCircle, MapPinned, Smartphone
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Page definitions with icons and descriptions
const SYSTEM_PAGES = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, description: "Main dashboard with analytics" },
  { id: "leads", label: "Leads", icon: Users, description: "Lead management and tracking" },
  { id: "contacts", label: "Contacts", icon: UserCircle, description: "Contact directory" },
  { id: "work_hub", label: "Work Hub · Overview", icon: Briefcase, description: "Work Hub landing: client work KPIs, monthly progress, overdue and workload" },
  { id: "clients", label: "Work Hub · Clients", icon: Briefcase, description: "Client management" },
  { id: "calendar", label: "Calendar", icon: Calendar, description: "Events and scheduling" },
  { id: "tasks", label: "Work Hub · Tasks", icon: CheckSquare, description: "Task management" },
  { id: "automation", label: "Automation", icon: Zap, description: "Workflow automation" },
  { id: "reports", label: "Reports", icon: BarChart3, description: "Analytics and reports" },
  { id: "integrations", label: "Integrations", icon: Plug, description: "Third-party integrations" },
  { id: "settings", label: "Settings", icon: Settings, description: "System settings" },
  { id: "work_planner", label: "Work Hub · Work Planner", icon: Calendar, description: "Monthly planning board, service targets and task generation" },
  { id: "work_calendar", label: "Work Hub · Calendar", icon: Calendar, description: "All client work on a Month / Week / Day calendar" },
  { id: "team_workload", label: "Work Hub · Team Workload", icon: Users, description: "Assigned, completed, pending and overdue work per employee" },
  { id: "work_reports", label: "Work Hub · Reports", icon: BarChart3, description: "Client, service and employee work reports with export" },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, description: "WhatsApp inbox, live chat, templates and broadcasts" },
  { id: "location_tracking", label: "Location Tracking", icon: MapPinned, description: "Live staff map, routes and GPS history from the Dnexus Staff app (sensitive)" },
];

// Module definitions
const SYSTEM_MODULES = [
  { id: "crm", label: "CRM Module", description: "Customer Relationship Management (Leads, Contacts, Clients)" },
  { id: "attendance", label: "Attendance Module", description: "Staff attendance and punch in/out" },
  { id: "payroll", label: "Payroll Module", description: "Salary management and payslips" },
  { id: "leave", label: "Leave Management", description: "Leave requests and approvals" },
  { id: "tasks", label: "Task Management", description: "Projects and task tracking" },
  { id: "reports", label: "Reports & Analytics", description: "Business reports and analytics" },
];

// Dashboard cards
const DASHBOARD_CARDS = [
  { id: "total_revenue", label: "Total Revenue", description: "Revenue from converted deals" },
  { id: "expected_revenue", label: "Expected Revenue", description: "Pipeline expected revenue" },
  { id: "lead_performance", label: "Lead Performance", description: "Lead metrics and conversion" },
  { id: "team_task_summary", label: "Team Task Summary", description: "Task completion metrics" },
  { id: "my_performance", label: "My Performance", description: "Personal performance score" },
  { id: "team_performance", label: "Team Performance", description: "Team leaderboard" },
  { id: "attendance_summary", label: "Attendance Summary", description: "Attendance overview" },
  { id: "salary_overview", label: "Salary Overview", description: "Salary status and details" },
];

// Dnexus Staff (mobile) app permissions. Stored in page_permissions as
// "full_access" / "no_access" and enforced by /api/mobile/* on every request.
// All off unless granted (Attendance is always on); staff only ever see their own data.
const STAFF_APP_GROUPS = [
  {
    id: "leads", title: "Leads", viewId: "app_leads_view",
    perms: [
      { id: "app_leads_view", label: "View Assigned Leads", description: "Shows the Leads tab in the app with the leads currently assigned to this employee" },
      { id: "app_leads_edit", label: "Edit Assigned Leads", description: "Change name, phone, email, city, company and stage of their assigned leads" },
      { id: "app_leads_remarks", label: "Add Remarks", description: "Add remarks to their assigned leads" },
      { id: "app_leads_followup", label: "Update Follow-up", description: "Schedule follow-ups and mark them done" },
      { id: "app_leads_create", label: "Add Leads", description: "Add new leads from the app (assigned to themselves; admins can choose who)" },
    ],
  },
  {
    id: "tasks", title: "Tasks", viewId: "app_tasks_view",
    perms: [
      { id: "app_tasks_view", label: "View My Tasks", description: "Shows the Tasks tab with tasks assigned to this employee" },
      { id: "app_tasks_update", label: "Update Status", description: "Move tasks between To Do / In Progress / Review (Completed goes to a manager for approval)" },
      { id: "app_tasks_comment", label: "Add Comments", description: "Comment on their assigned tasks" },
      { id: "app_tasks_create", label: "Add Tasks", description: "Create tasks from the app (for themselves; admins can assign anyone)" },
    ],
  },
  {
    id: "reports", title: "Reports", viewId: "app_reports_view",
    perms: [
      { id: "app_reports_view", label: "View My Reports", description: "Their own attendance, lead and task reports (only sections they have access to)" },
    ],
  },
];
const appPermOn = (perms, id) => !!perms[id] && perms[id] !== "no_access";

// Permission levels
const PERMISSION_LEVELS = [
  { value: "no_access", label: "No Access", color: "bg-red-100 text-red-700" },
  { value: "view_only", label: "View Only", color: "bg-yellow-100 text-yellow-700" },
  { value: "view_add", label: "View + Add", color: "bg-blue-100 text-blue-700" },
  { value: "view_add_edit", label: "View + Add + Edit", color: "bg-indigo-100 text-indigo-700" },
  { value: "full_access", label: "Full Access", color: "bg-green-100 text-green-700" },
];

const getRoleColor = (role) => {
  const colors = {
    super_admin: "bg-red-100 text-red-700",
    admin: "bg-purple-100 text-purple-700",
    manager: "bg-indigo-100 text-indigo-700",
    project_manager: "bg-blue-100 text-blue-700",
    team_leader: "bg-cyan-100 text-cyan-700",
    employee: "bg-green-100 text-green-700",
  };
  return colors[role] || "bg-gray-100 text-gray-700";
};

const getRoleLabel = (role) => {
  const labels = {
    super_admin: "Super Admin",
    admin: "Admin",
    manager: "Manager",
    project_manager: "Project Manager",
    team_leader: "Team Leader",
    employee: "Employee",
  };
  return labels[role] || role;
};

export default function AccessControlManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [businessUsers, setBusinessUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("pages");
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);

  // Permission states
  const [pagePermissions, setPagePermissions] = useState({});
  const [modulePermissions, setModulePermissions] = useState({});
  const [dashboardCards, setDashboardCards] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedBusinessId) {
      fetchBusinessUsers(selectedBusinessId);
    } else {
      setBusinessUsers([]);
      setSelectedUserId("");
      setSelectedUser(null);
    }
  }, [selectedBusinessId]);

  useEffect(() => {
    if (selectedUserId && businessUsers.length > 0) {
      const user = businessUsers.find(u => u.user_id === selectedUserId);
      setSelectedUser(user);
      if (user) {
        fetchUserPermissions(selectedUserId);
      }
    } else {
      setSelectedUser(null);
      resetPermissions();
    }
  }, [selectedUserId, businessUsers]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [userRes, businessRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/businesses`, { credentials: "include" })
      ]);

      if (userRes.ok) {
        const userData = await userRes.json();
        setCurrentUser(userData);
        if (userData.role !== "super_admin" && userData.role !== "admin") {
          toast.error("Access denied. Admins only.");
          return;
        }
        if (userData.role === "admin") {
          // Business Admin: locked to their own business — no cross-tenant selector
          let bizName = userData.business_name || "";
          if (!bizName && userData.business_id) {
            try {
              const bizRes = await fetch(`${BACKEND_URL}/api/businesses/${userData.business_id}`, { credentials: "include" });
              if (bizRes.ok) bizName = (await bizRes.json())?.name || "";
            } catch (_) {}
          }
          setBusinesses([{ business_id: userData.business_id, name: bizName || "My Business" }]);
          setSelectedBusinessId(userData.business_id || "");
          return;
        }
      }

      if (businessRes.ok) {
        const businessData = await businessRes.json();
        setBusinesses(businessData || []);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessUsers = async (businessId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/access-control/business/${businessId}/users`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setBusinessUsers(data.users || []);
      }
    } catch (error) {
      console.error("Error fetching business users:", error);
    }
  };

  const fetchUserPermissions = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/access-control/user/${userId}/permissions?business_id=${selectedBusinessId}`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setPagePermissions(data.page_permissions || {});
        setModulePermissions(data.module_permissions || {});
        setDashboardCards(data.dashboard_cards || {});
        setHasChanges(false);
      }
    } catch (error) {
      console.error("Error fetching user permissions:", error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/access-control/audit-logs?business_id=${selectedBusinessId}`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.logs || []);
      }
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    }
  };

  const resetPermissions = () => {
    setPagePermissions({});
    setModulePermissions({});
    setDashboardCards({});
    setHasChanges(false);
  };

  const handlePagePermissionChange = (pageId, level) => {
    setPagePermissions(prev => ({ ...prev, [pageId]: level }));
    setHasChanges(true);
  };

  const handleAppPermissionToggle = (group, permId) => {
    setPagePermissions(prev => {
      const next = { ...prev, [permId]: appPermOn(prev, permId) ? "no_access" : "full_access" };
      // Without View the feature is off, so turning View off clears its actions too.
      if (permId === group.viewId && next[permId] === "no_access") {
        group.perms.forEach(p => { next[p.id] = "no_access"; });
      }
      return next;
    });
    setHasChanges(true);
  };

  const handleModuleToggle = (moduleId) => {
    setModulePermissions(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
    setHasChanges(true);
  };

  const handleDashboardCardToggle = (cardId) => {
    setDashboardCards(prev => ({ ...prev, [cardId]: !prev[cardId] }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!selectedUserId || !selectedBusinessId) {
      toast.error("Please select a business and user");
      return;
    }

    // Prevent self-modification for super_admin
    if (selectedUser?.role === "super_admin") {
      toast.error("Cannot modify Super Admin permissions");
      return;
    }
    if (currentUser?.role === "admin" && selectedUser?.role === "admin") {
      toast.error("Business Admin cannot modify another admin account");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/access-control/user/${selectedUserId}/permissions`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: selectedBusinessId,
          page_permissions: pagePermissions,
          module_permissions: modulePermissions,
          dashboard_cards: dashboardCards
        })
      });

      if (res.ok) {
        toast.success("Permissions saved successfully! User will see changes on next login or page refresh.");
        setHasChanges(false);
        
        // If editing current user's permissions, refresh their session
        if (selectedUserId === currentUser?.user_id) {
          toast.info("Your own permissions were updated. Refreshing...");
          setTimeout(() => window.location.reload(), 1500);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to save permissions");
      }
    } catch (error) {
      console.error("Error saving permissions:", error);
      toast.error("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = businessUsers.filter(user => {
    // Business Admins never see Super Admin accounts, and cannot touch admin accounts
    if (currentUser?.role === "admin" && (user.role === "super_admin" || user.role === "admin")) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    );
  });

  // Access check
  if (currentUser && currentUser.role !== "super_admin" && currentUser.role !== "admin") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Access Denied</h2>
        <p className="text-slate-600 dark:text-slate-400">You don't have permission to access this page. Contact your Business Administrator.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-indigo-600" />
            Access Control Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage user permissions across pages, modules, and dashboard
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { fetchAuditLogs(); setLogsDialogOpen(true); }}
            disabled={!selectedBusinessId}
          >
            <History className="w-4 h-4 mr-2" />
            Audit Logs
          </Button>
          <Button onClick={fetchInitialData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Business Selection */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Select Business
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentUser?.role === "admin" ? (
            <div className="flex items-center gap-3 max-w-md p-3 rounded-lg border border-indigo-200 bg-indigo-50/60 dark:bg-indigo-900/20 dark:border-indigo-800">
              <Building2 className="w-5 h-5 text-indigo-600 shrink-0" />
              <div>
                <p className="font-semibold text-slate-800 dark:text-white">{businesses[0]?.name || "My Business"}</p>
                <p className="text-xs text-slate-500">As Business Admin you manage permissions for your business only</p>
              </div>
            </div>
          ) : (
            <Select value={selectedBusinessId} onValueChange={setSelectedBusinessId}>
              <SelectTrigger className="w-full max-w-md" data-testid="business-select">
                <SelectValue placeholder="Choose a business to manage" />
              </SelectTrigger>
              <SelectContent>
                {businesses.map(business => (
                  <SelectItem key={business.business_id} value={business.business_id}>
                    {business.name} - {business.city || "No city"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* User Selection */}
      {selectedBusinessId && (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Select User
            </CardTitle>
            <CardDescription>
              {businessUsers.length} user(s) in this business
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="user-search"
              />
            </div>

            {/* User List */}
            <div className="grid gap-2 max-h-[300px] overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-center py-8 text-slate-500">No users found</p>
              ) : (
                filteredUsers.map(user => (
                  <div
                    key={user.user_id}
                    onClick={() => user.role !== "super_admin" && setSelectedUserId(user.user_id)}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all
                      ${selectedUserId === user.user_id 
                        ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" 
                        : "border-slate-200 dark:border-slate-700 hover:border-indigo-300"}
                      ${user.role === "super_admin" ? "opacity-50 cursor-not-allowed" : ""}
                    `}
                    data-testid={`user-card-${user.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-medium">
                        {user.name?.charAt(0)?.toUpperCase() || "U"}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 dark:text-white">{user.name || "Unknown"}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getRoleColor(user.role)}>
                        {getRoleLabel(user.role)}
                      </Badge>
                      {user.role === "super_admin" && (
                        <Badge variant="outline" className="text-xs">Protected</Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Permission Configuration */}
      {selectedUser && selectedUser.role !== "super_admin" && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  Permissions for {selectedUser.name}
                </CardTitle>
                <CardDescription>
                  Configure access levels for this user
                </CardDescription>
              </div>
              {hasChanges && (
                <Badge className="bg-amber-100 text-amber-700">Unsaved Changes</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="pages" data-testid="tab-pages">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Pages
                </TabsTrigger>
                <TabsTrigger value="modules" data-testid="tab-modules">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Modules
                </TabsTrigger>
                <TabsTrigger value="dashboard" data-testid="tab-dashboard">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Dashboard
                </TabsTrigger>
                <TabsTrigger value="staff_app" data-testid="tab-staff-app">
                  <Smartphone className="w-4 h-4 mr-2" />
                  Staff App
                </TabsTrigger>
              </TabsList>

              {/* Pages Tab */}
              <TabsContent value="pages" className="space-y-4">
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Page</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-[200px]">Permission Level</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {SYSTEM_PAGES.map(page => {
                        const PageIcon = page.icon;
                        return (
                          <TableRow key={page.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <PageIcon className="w-4 h-4 text-slate-500" />
                                <span className="font-medium">{page.label}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-500 text-sm">
                              {page.description}
                            </TableCell>
                            <TableCell>
                              <Select
                                value={pagePermissions[page.id] || "no_access"}
                                onValueChange={(value) => handlePagePermissionChange(page.id, value)}
                              >
                                <SelectTrigger className="w-full" data-testid={`page-permission-${page.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {PERMISSION_LEVELS.map(level => (
                                    <SelectItem key={level.value} value={level.value}>
                                      <Badge className={level.color}>{level.label}</Badge>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Modules Tab */}
              <TabsContent value="modules" className="space-y-4">
                <p className="text-sm text-slate-500 mb-4">
                  Toggle modules ON/OFF. When OFF, the module is completely hidden from the sidebar.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  {SYSTEM_MODULES.map(module => (
                    <div
                      key={module.id}
                      className={`p-4 rounded-lg border transition-all ${
                        modulePermissions[module.id] !== false
                          ? "border-green-200 bg-green-50 dark:bg-green-900/20"
                          : "border-slate-200 bg-slate-50 dark:bg-slate-800/50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-800 dark:text-white">
                              {module.label}
                            </h4>
                            {modulePermissions[module.id] !== false ? (
                              <Eye className="w-4 h-4 text-green-600" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{module.description}</p>
                        </div>
                        <Switch
                          checked={modulePermissions[module.id] !== false}
                          onCheckedChange={() => handleModuleToggle(module.id)}
                          data-testid={`module-toggle-${module.id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Dashboard Tab */}
              <TabsContent value="dashboard" className="space-y-4">
                <p className="text-sm text-slate-500 mb-4">
                  Control which dashboard cards are visible to this user.
                </p>
                <div className="grid gap-3 md:grid-cols-2">
                  {DASHBOARD_CARDS.map(card => (
                    <div
                      key={card.id}
                      className={`p-4 rounded-lg border flex items-center justify-between transition-all ${
                        dashboardCards[card.id] !== false
                          ? "border-indigo-200 bg-indigo-50 dark:bg-indigo-900/20"
                          : "border-slate-200 bg-slate-50 dark:bg-slate-800/50"
                      }`}
                    >
                      <div>
                        <h4 className="font-medium text-slate-800 dark:text-white">
                          {card.label}
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">{card.description}</p>
                      </div>
                      <Switch
                        checked={dashboardCards[card.id] !== false}
                        onCheckedChange={() => handleDashboardCardToggle(card.id)}
                        data-testid={`dashboard-card-${card.id}`}
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Staff App Tab */}
              <TabsContent value="staff_app" className="space-y-4">
                <p className="text-sm text-slate-500 mb-4">
                  Features in the Dnexus Staff mobile app. Attendance is always on; everything below is off until you
                  turn it on. Staff only ever see their own leads, tasks and reports, and can't create, delete,
                  reassign or export anything from the app.
                </p>
                {STAFF_APP_GROUPS.map(group => (
                  <div key={group.id} className="space-y-2">
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 pt-2">{group.title}</h4>
                    <div className="grid gap-3 md:grid-cols-2">
                      {group.perms.map(perm => {
                        const on = appPermOn(pagePermissions, perm.id);
                        const locked = perm.id !== group.viewId && !appPermOn(pagePermissions, group.viewId);
                        return (
                          <div
                            key={perm.id}
                            className={`p-4 rounded-lg border flex items-center justify-between gap-4 transition-all ${
                              on && !locked
                                ? "border-green-200 bg-green-50 dark:bg-green-900/20"
                                : "border-slate-200 bg-slate-50 dark:bg-slate-800/50"
                            } ${locked ? "opacity-60" : ""}`}
                          >
                            <div>
                              <h4 className="font-medium text-slate-800 dark:text-white">{perm.label}</h4>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {locked ? `Turn on ${group.perms[0].label} first.` : perm.description}
                              </p>
                            </div>
                            <Switch
                              checked={on && !locked}
                              disabled={locked}
                              onCheckedChange={() => handleAppPermissionToggle(group, perm.id)}
                              data-testid={`app-permission-${perm.id}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </TabsContent>
            </Tabs>

            {/* Save Button */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => fetchUserPermissions(selectedUserId)}
                disabled={saving}
              >
                Reset Changes
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={saving || !hasChanges}
                data-testid="save-permissions-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit Logs Dialog */}
      <Dialog open={logsDialogOpen} onOpenChange={setLogsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Permission Change Audit Logs
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {auditLogs.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No audit logs found</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>User Affected</TableHead>
                    <TableHead>Change Type</TableHead>
                    <TableHead>Changed By</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log, idx) => (
                    <TableRow key={log.log_id || idx}>
                      <TableCell className="text-sm">
                        {log.created_at ? format(parseISO(log.created_at), "dd MMM yyyy HH:mm") : "-"}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{log.user_name || "Unknown"}</p>
                          <p className="text-xs text-slate-500">{log.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.change_type || "permission_update"}</Badge>
                      </TableCell>
                      <TableCell>{log.changed_by_name || "System"}</TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                        {log.details || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
